/**
 * @poc-to-production — Renders children only when APS session is valid.
 */
import type { ComponentChildren } from "preact";
import { useEffect } from "preact/hooks";
import { useAuthStore } from "../stores/authStore";
import { LoginScreen } from "./LoginScreen";

export function AuthGate({ children }: { children: ComponentChildren }) {
  const hydrate = useAuthStore(s => s.hydrate);
  const trySilentLogin = useAuthStore(s => s.trySilentLogin);
  const status = useAuthStore(s => s.status);
  const accessToken = useAuthStore(s => s.accessToken);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  useEffect(() => {
    if (status === "access_denied") return;
    if (status !== "idle" || accessToken != null) return;
    void trySilentLogin();
  }, [status, accessToken, trySilentLogin]);

  const authenticated = status === "authenticated" && accessToken != null;

  if (status === "loading") {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--fc2-bg)",
          color: "var(--fc2-text-dim)",
        }}
      >
        Completing sign-in…
      </div>
    );
  }

  if (status === "redirecting" && !authenticated) {
    return (
      <div
        style={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          background: "var(--fc2-bg)",
          color: "var(--fc2-text-dim)",
        }}
      >
        Checking Autodesk session…
      </div>
    );
  }

  if (!authenticated) {
    return <LoginScreen />;
  }

  return <>{children}</>;
}
